import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import useTranslations from 'hooks/useTranslations'
import { ProductAttributes, Props, SizeAttribute } from './types'
import useExpectedSizeChartAttributes from './hooks/useExpectedSizeChartAttributes'
import useSizeChart from './hooks/useSizeChart'
import { useEffect, useRef, useState } from 'react'
import Swipe from 'assets/swipe.svg'

export default function ProductSizes(props: Props) {
  const t = useTranslations<'templates-PDP'>()

  const expectedSizeChartAttributes = useExpectedSizeChartAttributes()

  const sizeChart: ProductAttributes[] = useSizeChart(
    props.sizeChartData.sizeChartKey,
    expectedSizeChartAttributes
  )

  // Calculate the number of table headers that we have attributes for in the props.
  const tableHeaderCount = expectedSizeChartAttributes.size

  // Sort the table headers by their sorting value and put them into an array.
  const tableHeaders: { label: string; sorting: number }[] = []

  expectedSizeChartAttributes.forEach((kv) => {
    tableHeaders.push({
      label: kv.label,
      sorting: kv.sorting
    })
  })
  tableHeaders.sort((a, b) => {
    return a.sorting - b.sorting
  })

  const [tableIsOverflowing, setTableIsOverflowing] = useState(false)
  const tableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkOverflow = () => {
      if (tableRef.current) {
        const hasOverflow =
          tableRef.current.scrollHeight > tableRef.current.clientHeight ||
          tableRef.current.scrollWidth > tableRef.current.clientWidth
        setTableIsOverflowing(hasOverflow)
      }
    }

    checkOverflow() // Check overflow on mount

    // Re-check on window resize (if overflow might change dynamically)
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [])

  return (
    <Wrapper
      data-cy-collection="ProductSizes"
      className="ProductSizes"
      data-cy-state={props['data-cy-state']}
      $tableHeaderCount={tableHeaderCount}
    >
      <div className="product_sizes_heading_wrapper">
        <h3 className="product_sizes_heading">
          {t('sizeChart_product_size_table_title')}
        </h3>

        {tableIsOverflowing && (
          <div className="swipe_icon_wrapper" data-cy-state="show-swipe-icon">
            <Swipe></Swipe>
            <p>{t('sizeChart_scrollable_label')}</p>
          </div>
        )}
      </div>
      <div className="product_sizes_table" ref={tableRef}>
        <table>
          <tr>
            <th>{t('size_label')}</th>

            {tableHeaders.map((header, index) => {
              return <th key={index}>{header.label}</th>
            })}
          </tr>

          {/* eslint-disable @kaminrunde/firescout/jsx-expression-needs-state */}
          {tableHeaderCount > 0 &&
            sizeChart.map((item) => (
              <tr key={item.size}>
                {item.size && <td>{item.size}</td>}
                {item.sizeAttributes.map((attribute, index) => (
                  <td key={index}>
                    {attribute.value > 0
                      ? attribute.value + ' ' + attribute.unit
                      : ''}
                  </td>
                ))}
              </tr>
            ))}
        </table>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $tableHeaderCount: number }>`
  margin-top: ${theme.spacing('m')};
  border-radius: ${theme.spacing('xs')};
  background-color: ${theme.colors.white};
  width: 100%;

  > .product_sizes_heading_wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    > .product_sizes_heading {
      margin-bottom: ${theme.spacing('m')};
    }

    > .swipe_icon_wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 1.25rem;

      > p {
        margin: 0;
        margin-left: 0.5rem;
      }
    }
  }

  > .product_sizes_table {
    width: 100%;
    overflow-x: auto;
  }

  > .product_sizes_table table {
    width: 100%;
    min-height: auto;
    min-width: 43.75rem;
    border-collapse: collapse;
    table-layout: fixed;

    > th {
      text-align: center;
      width: 25%;
      height: 3.125rem;
      table-layout: fixed;
    }

    > tr {
      text-align: center;
      height: 3.125rem;
      table-layout: fixed;

      > td {
        width: calc(100% / ${(p) => p.$tableHeaderCount});

        text-align: center;
        height: 3.125rem;
        table-layout: fixed;
      }
    }

    > tr:nth-child(odd) {
      background-color: ${theme.colors.shade.b6};
    }

    > tr:nth-child(even) {
      background-color: ${theme.colors.white};
    }
  }
`
